import { useDispatch, useSelector } from "react-redux";
import {
  matchPath,
  Redirect,
  useHistory,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import {
  getRouteWithoutPath,
  isOnboardingPath,
  useQueryGroupId,
} from "utils/routes";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  ACCOUNT_EXTRA_DETAILS_PATH,
  AFFILIATION_PATH,
  BACKTESTING_EXTRA_DETAILS_PATH,
  BASE_BILLING_SETTINGS_PATH,
  BASE_BOT_FORM_PATH,
  BASE_PUBLIC_EDIT_BOT_PATH,
  BASE_PURCHASE_SUBSCRIPTION_SETTINGS_PATH,
  BOT_EXTRA_DETAILS_PATH,
  BOTTOM_SUMMARY_CLASS,
  BUSINESS_PROFILE_PATH,
  CANCEL_SUBSCRIPTION_PATH,
  COIN_EXTRA_DETAILS_PATH,
  CONTENT_WRAPPER_ID,
  CREATE_TRADE_PATH,
  INVOICES_PATH,
  IS_WEB_VIEW,
  MAIN_CONTENT_ID,
  MARKETPLACE_GROUP_PATH,
  NOT_GROUP_MEMBER_MESSAGE,
  ONBOARDING_CONNECT_EXTERNAL_ACCOUNT_PATH,
  ONBOARDING_DCA_BOT_OVERVIEW_PATH,
  ONBOARDING_SIGNALS_GROUP_OVERVIEW_PATH,
  PAGE_SPINNER_MARGIN_FROM_TOP,
  PURCHASE_SUBSCRIPTION_SETTINGS_PATH,
  SETTINGS_PATH,
  SIGNAL_EXTRA_DETAILS_PATH,
  TRADE_EXTRA_DETAILS_PATH,
  USERS_MANAGEMENT_PATH,
} from "common/constants";
import useRoutesMatch from "utils/hooks/useRoutesMatch";
import { useNetworkState } from "react-use";
import { PageWrapperContext } from "common/Home/PageWrapper/PageWrapper.context.tsx";
import {
  useLiveGroupSearch,
  usePrevious,
  useQueryWithRetries,
  useUser,
} from "@shared/utils/hooks/misc";
import useQuery from "utils/hooks/useQuery";
import { openMessage } from "utils/misc";
import {
  ApiTags,
  FilterType,
  SubscriptionPlanType,
  WorkspaceType,
} from "@shared/types";
import { useCheckQuotaUsage } from "@shared/features/settings/hooks";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { IS_RESTRICTED_COUNTRY } from "utils/hooks/useRestrictedCountries";
import localStorageService from "utils/localStorageService";
import { PageLayout } from "types";
import { FILTER_TYPES_DATA } from "@shared/features/filters/constants";
import { SUBSCRIPTION_QUERY_PARAMS } from "@shared/features/settings/subscription/constants";
import {
  getParsedQueryPlan,
  getWhitelabelPlanConflictError,
} from "@shared/features/settings/subscription/utils";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import { sharedLib } from "@shared/services/shared.service";
import { difference } from "lodash";
import { getSignalsFiltersSlice } from "@shared/features/signals/common/utils";
import * as Sentry from "@sentry/react";
import { BottomNavigatorContext } from "features/misc/BottomNavigatorProvider.tsx";
import { useFreezeContext } from "common/Home/MobileHome/DelayedFreeze.tsx";
import {
  selectIsProcessingQuickConnect,
  selectPollClientsListData,
  selectWebviewKeyboardStatus,
  selectWorkspace,
} from "@shared/slices/common.slice.ts";
import { KeyboardStatus } from "@shared/services/webview/types.ts";
import useCreateAccountMessage from "features/configuration/accounts/CreateAccount/useCreateAccountMessage.tsx";

import {
  QUICK_CONNECT_CONN_CODE_QUERY_PARAM,
  QUICK_CONNECT_STATE_QUERY_PARAM,
} from "features/configuration/common/constants";

const LEGACY_TABLE_PAGE_LAYOUT = 0;

export const useShowWorkspaceSwitch = () => {
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const isOnboarding = useIsOnboarding();

  const isSettingsPath = useRoutesMatch(
    [SETTINGS_PATH, AFFILIATION_PATH],
    false
  );
  const isBusinessOperationsPath = useRoutesMatch(
    [INVOICES_PATH, BUSINESS_PROFILE_PATH, USERS_MANAGEMENT_PATH],
    false
  );
  const isBillingPath = !!useRoutesMatch([BASE_BILLING_SETTINGS_PATH], false);

  const isSubUser = user?.is_sub_user;
  const isRestrictedWhopUser = user?.isRestrictedWhopUser();

  return (
    !isOnboarding &&
    !isSubUser &&
    !isRestrictedWhopUser &&
    (isBillingPath || (!isSettingsPath && !isBusinessOperationsPath))
  );
};

export const useIsOnboarding = () => {
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const location = useLocation();

  return !user || user?.is_onboarding || isOnboardingPath(location);
};

export const useIsProcessingQuickConnect = () => {
  const isProcessingQuickConnect = useSelector(selectIsProcessingQuickConnect);
  const query = useQuery();

  if (isProcessingQuickConnect !== undefined) {
    return isProcessingQuickConnect;
  }

  return (
    !!query.get(QUICK_CONNECT_CONN_CODE_QUERY_PARAM) &&
    !!query.get(QUICK_CONNECT_STATE_QUERY_PARAM)
  );
};

export const scrollToTop = () => {
  const scrollingElement = document.getElementById(MAIN_CONTENT_ID);
  if (scrollingElement && scrollingElement.scrollTop >= 1) {
    scrollingElement.scroll(0, 0);
  }
};

export const useScrollToTop = () => {
  const location = useLocation();
  const DRAWERS_PATHS = [
    COIN_EXTRA_DETAILS_PATH,
    ACCOUNT_EXTRA_DETAILS_PATH,
    TRADE_EXTRA_DETAILS_PATH,
    BOT_EXTRA_DETAILS_PATH,
    SIGNAL_EXTRA_DETAILS_PATH,
    MARKETPLACE_GROUP_PATH,
    BACKTESTING_EXTRA_DETAILS_PATH,
    PURCHASE_SUBSCRIPTION_SETTINGS_PATH,
  ];
  const DELAY_SCROLLING_PATHS = [
    ONBOARDING_DCA_BOT_OVERVIEW_PATH,
    ONBOARDING_SIGNALS_GROUP_OVERVIEW_PATH,
    ONBOARDING_CONNECT_EXTERNAL_ACCOUNT_PATH,
  ];
  const isDrawerPath = Boolean(useRoutesMatch(DRAWERS_PATHS));
  const previousIsDrawerPath = useRef(isDrawerPath);
  const isDelayScrolling = Boolean(useRoutesMatch(DELAY_SCROLLING_PATHS));

  useEffect(() => {
    if (isDelayScrolling) {
      setTimeout(() => {
        scrollToTop();
      }, 300);
    } else if (!isDrawerPath && !previousIsDrawerPath.current) {
      scrollToTop();
    }
    previousIsDrawerPath.current = isDrawerPath;
  }, [location.pathname, isDrawerPath]);
};

export const useBaseQueries = () => {
  const user = useSelector(sharedLib.selectors.authentication.selectUser);

  const refetchIntervalMinutes = 10;
  const options = {
    pollingInterval: refetchIntervalMinutes * 60 * 1000,
    skip: user?.is_onboarding,
  };
  useQueryWithRetries(
    sharedLib.sharedApi.common.useGetPrecisionsQuery,
    undefined,
    options
  );
  useQueryWithRetries(
    sharedLib.sharedApi.common.useGetSymbolsMetadataQuery,
    undefined,
    options
  );
  sharedLib.sharedApi.authentication.useGetUserLiteQuery(undefined, {
    pollingInterval: 5 * 60 * 1000,
  });
};

export const useOnReconnect = (onReconnect?: () => void) => {
  const networkState = useNetworkState();
  useEffect(() => {
    if (networkState.online && !networkState.previous) {
      try {
        onReconnect?.();
      } catch {}
    }
  }, [networkState]);
};

export const useHeaderSearchValue = (searchValue?: string) => {
  const { useMobileSearch: useHeaderSearch, search: headerSearch } =
    useContext(PageWrapperContext);

  return useHeaderSearch ? headerSearch.value : searchValue;
};

export const useHotjar = () => {
  const { user } = useUser(true);
  const [initializeUserId, setInitializedUserId] = useState<number>();

  useEffect(() => {
    if (user && user.id !== initializeUserId) {
      // @ts-ignore
      window.hj?.("identify", user.id, { user_id: user.id });
      // @ts-ignore
      setInitializedUserId(user.id);
    }
  }, [user]);
};

export const useSentryUser = () => {
  const { user } = useUser(true);
  const [initializeUserId, setInitializedUserId] = useState<number>();

  useEffect(() => {
    try {
      if (user && user.id !== initializeUserId) {
        Sentry.setUser({ id: user.id });
        setInitializedUserId(user.id);
      } else if (!user) {
        Sentry.setUser(null);
      }
    } catch {}
  }, [user]);
};

export const useDisplayLinkMessages = () => {
  const query = useQuery();
  const code = query.get("code");
  const message = query.get("message");

  useEffect(() => {
    if (code && code !== "200" && message) {
      openMessage(decodeURIComponent(message), "error");
    }
  }, [code, message]);
};

export const useRestrictedCountriesPurchase = () => {
  const history = useHistory();

  const onPurchaseClick = (nextPath: string) => {
    if (IS_RESTRICTED_COUNTRY) {
      openMessage(
        "Unfortunately, as stated in our Terms of Use, we do not currently provide services to users from your country.",
        "error"
      );
    } else history.push(nextPath);
  };

  return { onPurchaseClick };
};

export const useDrawerDelayedUnmount = <T extends number | string>(
  objectId?: T
) => {
  const [localObjectId, setLocalObjectId] = useState(objectId);
  const [isUnmounting, setIsUnmounting] = useState(false);

  useEffect(() => {
    let clearLoadingTimeout: NodeJS.Timeout;
    if (objectId) {
      setLocalObjectId(objectId);
      setIsUnmounting(false);
    } else if (localObjectId) {
      setIsUnmounting(true);
      clearLoadingTimeout = setTimeout(() => {
        setLocalObjectId(undefined);
        setIsUnmounting(false);
      }, 300);
    }
    return () => {
      if (clearLoadingTimeout) {
        clearTimeout(clearLoadingTimeout);
        setIsUnmounting(false);
      }
    };
  }, [objectId]);

  return { id: localObjectId, isUnmounting };
};

export const useHideBottomSpacer = (hide = true) => {
  const { setShouldHideBottomSpacer } = useContext(PageWrapperContext)!;

  useEffect(() => {
    if (hide) {
      setShouldHideBottomSpacer(true);
      return () => {
        setShouldHideBottomSpacer(false);
      };
    }
  }, [hide]);
};

export const useDisablePageScroll = (disable = true) => {
  const { freezing } = useFreezeContext();

  useEffect(() => {
    if (freezing) return;
    const mainContent = document.getElementById(MAIN_CONTENT_ID);
    const contentWrapper = document.getElementById(CONTENT_WRAPPER_ID);
    if (disable && mainContent && contentWrapper) {
      mainContent.scrollTop = 0;
      mainContent.style.overflowY = "hidden";
      contentWrapper.style.minHeight = "initial";
      contentWrapper.style.height = "100%";
      return () => {
        mainContent.style.overflowY = "auto";
        contentWrapper.style.minHeight = "100%";
        contentWrapper.style.height = "initial";
      };
    }
  }, [disable, freezing]);
};

export const useCheckBillingRedirects = () => {
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const query = useQuery();
  const queryPlan = getParsedQueryPlan(
    query.get(SUBSCRIPTION_QUERY_PARAMS.PLAN)
  ) as number;
  const isAllPlans = !!query.get(SUBSCRIPTION_QUERY_PARAMS.IS_ALL_PLANS);
  const isUpgrade = !!query.get(SUBSCRIPTION_QUERY_PARAMS.IS_UPGRADE);
  const checkQuotaImmediately =
    !!user &&
    !!queryPlan &&
    user.subscription_plan_type > queryPlan &&
    (!user?.isExpired() || user?.in_view_only_grace_period);
  const { isLoading, isQuotaError } = useCheckQuotaUsage(
    checkQuotaImmediately,
    queryPlan as SubscriptionPlanType
  );
  if (!user) return;

  let message =
    "The URL you've tried to reach doesn't match your current subscription status";

  const testPath = (path: string, exact = true) =>
    !!matchPath(location.pathname, {
      path,
      exact,
      strict: false,
    });

  const isTrial = user.is_trial;
  const isExpired = user.isExpired();
  let redirectPath: string;
  if (isTrial) {
    redirectPath = BASE_PURCHASE_SUBSCRIPTION_SETTINGS_PATH;
    if (isAllPlans)
      redirectPath += `?${SUBSCRIPTION_QUERY_PARAMS.IS_ALL_PLANS}=1`;
  } else {
    redirectPath = BASE_BILLING_SETTINGS_PATH;
  }
  const isMaxSubscriptionPlan = user?.isMaxSubscription();
  const isExtendPath = testPath(BASE_PURCHASE_SUBSCRIPTION_SETTINGS_PATH);
  const isCancelSubscription = testPath(CANCEL_SUBSCRIPTION_PATH);

  const getNoMatchExtendPath = () => {
    if (!isExtendPath) return false;
    return (
      !isTrial &&
      !user.isFree() &&
      !isExpired &&
      !!queryPlan &&
      queryPlan > user.subscription_plan_type &&
      !isUpgrade
    );
  };

  const noMatchExtendPath = getNoMatchExtendPath();
  const noMatchUpgradePath =
    isUpgrade &&
    (isTrial ||
      isExpired ||
      isMaxSubscriptionPlan ||
      (!!queryPlan && user.subscription_plan_type >= queryPlan));
  const noMatchQuota = isExtendPath && isQuotaError;
  const whitelabelConflictError = getWhitelabelPlanConflictError(
    user.subscription_plan_type,
    queryPlan as SubscriptionPlanType
  );

  if (!isCancelSubscription) {
    if (whitelabelConflictError) {
      openMessage(whitelabelConflictError, "warning");
      return <Redirect to={redirectPath} />;
    } else if (noMatchExtendPath || noMatchUpgradePath || noMatchQuota) {
      openMessage(message, "warning");
      return <Redirect to={redirectPath} />;
    } else if (isLoading) {
      return (
        <LoadingSpinnerOverlay
          isLoading={true}
          spinnerSize="large"
          spinnerMarginTop={PAGE_SPINNER_MARGIN_FROM_TOP}
        />
      );
    }
  }
};

export const useLayoutChangeButtons = (storageKey: string) => {
  const storedPageLayout = localStorageService.getParsedValue<PageLayout | 0>(
    storageKey
  );
  const defaultPageLayout =
    storedPageLayout === LEGACY_TABLE_PAGE_LAYOUT
      ? PageLayout.TABLE
      : storedPageLayout;
  const [isCardsView, setIsCardsView] = useState(
    defaultPageLayout === null || defaultPageLayout === PageLayout.CARDS
  );

  useEffect(() => {
    if (storedPageLayout === LEGACY_TABLE_PAGE_LAYOUT) {
      localStorageService.setValue(storageKey, PageLayout.TABLE);
    }
  }, [storageKey, storedPageLayout]);

  const setIsCardsViewAndStorage = useCallback(
    (isCardsView: boolean) => {
      setIsCardsView(isCardsView);
      localStorageService.setValue(
        storageKey,
        isCardsView ? PageLayout.CARDS : PageLayout.TABLE
      );
    },
    [storageKey]
  );

  return {
    isCardsView,
    setIsCardsView: setIsCardsViewAndStorage,
  };
};

export const useNavigateToTradesPage = () => {
  const history = useHistory();

  const navigateWithQuery = (
    location: string,
    filterTypeToValue: { [key in FilterType]?: any }
  ) => {
    const updatedQuery = new URLSearchParams();
    Object.keys(filterTypeToValue).forEach((filterType) => {
      const value = filterTypeToValue[filterType];
      updatedQuery.set(
        FILTER_TYPES_DATA[FilterType.SOURCE_NAME].queryKey,
        JSON.stringify([value])
      );
    });
    history.push(`${location}?${updatedQuery.toString()}`);
  };

  return { navigateWithQuery };
};

export const useBottomNavigator = () => {
  return useContext(BottomNavigatorContext)!;
};

export const useIsWindowFocused = () => {
  const [focused, setFocused] = useState(!document.hidden);

  const onVisibilityChange = () => {
    setFocused(!document.hidden);
  };

  const onFocused = () => {
    setFocused(true);
  };

  const onBlur = () => {
    setFocused(false);
  };

  useEffect(() => {
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onFocused);
    window.addEventListener("blur", onBlur);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onFocused);
      window.removeEventListener("blur", onBlur);
    };
  }, []);

  return focused;
};

export const usePollClientsList = () => {
  const monitorStartTime = useRef<number | undefined>(undefined);
  const focused = useIsWindowFocused();
  const previousFocused = usePrevious(focused);
  const [initialAccountIds, setInitialAccountIds] = useState<
    number[] | undefined
  >(undefined);
  const { stopPolling, pollClientsListData } = usePollClientsListData();
  const { data: accounts } =
    sharedLib.sharedApi.configuration.endpoints.getClientsList.useQueryState();
  const [getClientsList] =
    sharedLib.sharedApi.configuration.useLazyGetClientsListQuery();
  const dispatch = useDispatch();
  const { getSuccessMessage, contextHolder: successContextHolder } =
    useCreateAccountMessage();
  const isExpired =
    !!monitorStartTime.current &&
    performance.now() - monitorStartTime.current > 1000 * 60 * 10;
  const shouldPoll = pollClientsListData?.shouldPoll;
  const workspace = useSelector(selectWorkspace);
  const isUserWorkspace = workspace === WorkspaceType.USER;

  const handleStopPolling = (accountsCreated?: boolean) => {
    stopPolling(accountsCreated);
    monitorStartTime.current = undefined;
  };

  const refetchData = () => {
    if (!isUserWorkspace) return;
    getClientsList()
      .unwrap()
      .then((accountsList) => {
        const accountIds = accountsList?.map((account) => account.client_id);
        const addedAccountsList = difference(
          accountIds,
          initialAccountIds || []
        );
        if (addedAccountsList.length) {
          getSuccessMessage(
            undefined,
            accountsList
              ?.filter((account) =>
                addedAccountsList.includes(account.client_id)
              )
              .map((account) => account.exchange)
          );
          handleStopPolling(true);
          dispatch(
            sharedLib.sharedApi.authentication.util.invalidateTags([
              { type: ApiTags.USER, id: "LIST" },
            ])
          );
          dispatch(
            sharedLib.sharedApi.portfolio.util.invalidateTags([
              { type: ApiTags.PORTFOLIO_DATA, id: "LIST" },
            ])
          );
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (isExpired) {
      handleStopPolling();
    } else if (shouldPoll && focused && !previousFocused && isUserWorkspace) {
      refetchData();
    }
  }, [shouldPoll, focused, isUserWorkspace]);

  useEffect(() => {
    if (pollClientsListData?.shouldPoll && isUserWorkspace) {
      monitorStartTime.current = performance.now();
      setInitialAccountIds(accounts?.map((account) => account.client_id) || []);
    }
  }, [pollClientsListData, isUserWorkspace]);

  return {
    successContextHolder,
  };
};

export const usePollClientsListData = () => {
  const dispatch = useDispatch();
  const pollClientsListData = useSelector(selectPollClientsListData);

  const startPolling = () => {
    dispatch(
      sharedLib.slices.common.actions.setPollClientsListData({
        shouldPoll: true,
      })
    );
  };

  const stopPolling = (accountsCreated?: boolean) => {
    dispatch(
      sharedLib.slices.common.actions.setPollClientsListData({
        shouldPoll: false,
        accountsCreated,
      })
    );
  };

  return {
    startPolling,
    stopPolling,
    pollClientsListData,
  };
};

interface useCheckUserMembershipProps {
  isAdminSignals?: boolean;
  isOpen?: boolean;
}

export const useCheckUserMembership = ({
  isAdminSignals,
  isOpen,
}: useCheckUserMembershipProps) => {
  const location = useLocation();
  const { historyPush } = useHistoryWithQuery();
  const dispatch = useDispatch();
  const querySubGroupId = useQueryGroupId();
  const [originalQuerySubGroupId] = useState(querySubGroupId);
  const filtersSlice = getSignalsFiltersSlice(isOpen, isAdminSignals);
  const loadedFilters = useSelector(
    filtersSlice.selectors.selectFilters
  ).loadedFilters;
  const groupsFilterData = useSelector(
    filtersSlice.selectors.selectFiltersData
  )[FilterType.GROUPS];
  const { isNotMember, isFetching, liveUpdateChannelsQuery } =
    useLiveGroupSearch(originalQuerySubGroupId);
  const isSignalExtraDetails = useRouteMatch(SIGNAL_EXTRA_DETAILS_PATH);

  useEffect(() => {
    if (isNotMember) {
      openMessage(NOT_GROUP_MEMBER_MESSAGE, "warning", 8);
      if (isSignalExtraDetails) {
        historyPush(
          getRouteWithoutPath(location.pathname, SIGNAL_EXTRA_DETAILS_PATH)
        );
      }
    }
  }, [isNotMember]);

  useEffect(() => {
    if (loadedFilters && isNotMember) {
      const defaultGroupId = groupsFilterData?.options[0];
      dispatch(filtersSlice.actions.setGroupsFilter([defaultGroupId]));
    }
  }, [isNotMember, loadedFilters]);

  return {
    isFetching,
    liveUpdateChannelsQuery,
  };
};

export const useVirtualKeyboard = (skip?: boolean) => {
  const [isKeyboardOpen, setKeyboardOpen] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const defaultSelector = useCallback(() => KeyboardStatus.DID_HIDE, []);
  const webviewKeyboardStatus = useSelector(
    skip ? defaultSelector : selectWebviewKeyboardStatus
  );
  const isWebviewKeyboardOpen =
    webviewKeyboardStatus === KeyboardStatus.DID_SHOW ||
    webviewKeyboardStatus === KeyboardStatus.WILL_SHOW;

  useEffect(() => {
    if (IS_WEB_VIEW || skip) return;
    const visualViewport = window.visualViewport;

    if (visualViewport) {
      const onResize = () => {
        const focusedElement = document.activeElement as HTMLElement | null;

        // Bail if no element is focused as that also means no input is focused
        if (!focusedElement) return;

        const isInputFocused =
          focusedElement.tagName === "INPUT" ||
          focusedElement.tagName === "TEXTAREA";

        // Virtual keyboard should only be visible if an input is focused
        if (isInputFocused && visualViewport.height < window.innerHeight) {
          setKeyboardOpen(true);
          setKeyboardHeight(window.innerHeight - visualViewport.height);
        } else if (isKeyboardOpen) {
          // Reset keyboard height if it was open
          setKeyboardOpen(false);
          setKeyboardHeight(0);
        }
      };

      visualViewport.addEventListener("resize", onResize);

      return () => {
        visualViewport.removeEventListener("resize", onResize);
      };
    }
  }, [isKeyboardOpen, skip]);

  return {
    keyboardHeight,
    isKeyboardOpen: IS_WEB_VIEW ? isWebviewKeyboardOpen : isKeyboardOpen,
  };
};

export const useScrollToInputs = () => {
  const webviewKeyboardStatus = useSelector(selectWebviewKeyboardStatus);
  const keyboardDidShow = webviewKeyboardStatus === KeyboardStatus.DID_SHOW;

  const handleScrollToInput = () => {
    const target = document.activeElement as HTMLElement;
    if (!target || !["INPUT", "TEXTAREA"].includes(target.tagName)) return;

    const rect = target.getBoundingClientRect();
    const bottomOverlays = document.querySelectorAll(
      `.${BOTTOM_SUMMARY_CLASS}`
    );
    let totalOverlayHeight = 0;
    bottomOverlays.forEach((overlay) => {
      const overlayElement = overlay as HTMLElement;
      if (
        overlayElement.offsetHeight > 0 &&
        getComputedStyle(overlayElement).display !== "none"
      ) {
        totalOverlayHeight += overlayElement.offsetHeight;
      }
    });
    const isVisible =
      rect.top >= 0 &&
      rect.bottom <= window.innerHeight - totalOverlayHeight - 10;
    if (!isVisible) {
      target.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  useEffect(() => {
    if (!IS_WEB_VIEW || !keyboardDidShow) return;
    handleScrollToInput();
  }, [keyboardDidShow]);
};

export const useIsHideBottomNavigatorPath = () => {
  return !!useRoutesMatch(
    [BASE_BOT_FORM_PATH, BASE_PUBLIC_EDIT_BOT_PATH, CREATE_TRADE_PATH],
    false
  );
};
